"use client";

import ErrorMessage from "@/components/common/error-message";
import LoadingText from "@/components/common/loading-text";
import useCommunityAndChannelParams from "@/hooks/useChannelAndCommunityParams";
import { COMMUNITY_MEMBERS_API } from "@/lib/apis";
import { IGetCommunityMembersResponseDTO } from "@/types/community";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import CommunityMemberCard from "./members-card";
import CommunityMembersModal from "./members-modal";

const fetchCommunityMembers = async (communitySlug: string, channelSlug?: string) => {
	const url = new URL(COMMUNITY_MEMBERS_API);
	url.searchParams.set("community-slug", communitySlug);

	if (channelSlug) {
		url.searchParams.set("channel-slug", channelSlug);
	}

	const response = await fetch(url);

	const data = await response.json();

	return data;
};

const CommunityMembers = () => {
	const { communitySlug, channelSlug } = useCommunityAndChannelParams();

	const [showAllMembersModal, setShowAllMembersModal] = useState<boolean>(false);

	const { data, isLoading, isError } = useQuery<IGetCommunityMembersResponseDTO>({
		queryKey: ["community-members", communitySlug, channelSlug],
		queryFn: () => fetchCommunityMembers(communitySlug, channelSlug),
		refetchOnWindowFocus: false,
	});

	const members = data?.data?.members || [];

	return (
		<div className="relative rounded-lg bg-white p-4 shadow">
			<div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b pb-2">
				<h4 className="text-sm font-bold uppercase text-brand-primary">Community members</h4>

				<button
					type="button"
					className="text-sm font-medium text-gray-500"
					onClick={() => setShowAllMembersModal(true)}
				>
					View all
				</button>
			</div>

			{isLoading ? (
				<LoadingText />
			) : isError ? (
				<ErrorMessage content="Could not fetch community members" />
			) : members.length > 0 ? (
				<div className="space-y-4">
					{members.map((member, i) => (
						<CommunityMemberCard key={`${member.username}_${i}`} member={member} />
					))}
				</div>
			) : (
				<p className="text-center text-sm text-gray-400">No members</p>
			)}

			<CommunityMembersModal open={showAllMembersModal} setOpen={setShowAllMembersModal} />
		</div>
	);
};

export default CommunityMembers;
